import { useState, useEffect } from 'react';
import Modal from '../Modal.jsx';
import { formatCurrency } from '../../lib/format.js';

const EMPTY = {
  from_entity_id: '',
  to_entity_id: '',
  label: '',
  amount_ht_euros: '',
  vat_rate: '20',
  notes: '',
};

function validate(fields) {
  const errors = {};
  if (!fields.from_entity_id) errors.from_entity_id = 'Entité émettrice requise';
  if (!fields.to_entity_id) errors.to_entity_id = 'Entité débitrice requise';
  if (fields.from_entity_id && fields.to_entity_id && fields.from_entity_id === fields.to_entity_id) {
    errors.to_entity_id = "L'émettrice et la débitrice doivent être différentes";
  }
  const ht = parseFloat(fields.amount_ht_euros.replace(',', '.'));
  if (!fields.amount_ht_euros || isNaN(ht) || ht <= 0) {
    errors.amount_ht_euros = 'Montant HT requis (> 0)';
  }
  const vat = parseFloat(fields.vat_rate);
  if (isNaN(vat) || vat < 0 || vat > 100) {
    errors.vat_rate = 'TVA entre 0 et 100';
  }
  return errors;
}

export default function ServiceLineModal({ open, onClose, onSave, line, entities = [], isPending }) {
  const isEdit = Boolean(line);

  const [fields, setFields] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setErrors({});
      if (line) {
        setFields({
          from_entity_id: line.from_entity_id ?? '',
          to_entity_id: line.to_entity_id ?? '',
          label: line.label ?? '',
          amount_ht_euros: line.amount_ht_cents != null
            ? (line.amount_ht_cents / 100).toFixed(2)
            : '',
          vat_rate: line.vat_rate != null ? String(line.vat_rate) : '20',
          notes: line.notes ?? '',
        });
      } else {
        setFields(EMPTY);
      }
    }
  }, [open, line]);

  function set(key, value) {
    setFields((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  // Live TTC preview
  const htEuros = parseFloat(fields.amount_ht_euros.replace(',', '.'));
  const vatRate = parseFloat(fields.vat_rate);
  const htCents = !isNaN(htEuros) && htEuros > 0 ? Math.round(htEuros * 100) : null;
  const vatCents = htCents != null && !isNaN(vatRate) ? Math.round(htCents * vatRate / 100) : null;
  const ttcCents = htCents != null && vatCents != null ? htCents + vatCents : null;

  function handleSubmit(e) {
    e.preventDefault();
    const errs = validate(fields);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    const ht = parseFloat(fields.amount_ht_euros.replace(',', '.'));
    const htC = Math.round(ht * 100);
    const vatR = parseFloat(fields.vat_rate);
    onSave({
      from_entity_id: fields.from_entity_id,
      to_entity_id: fields.to_entity_id,
      label: fields.label.trim() || undefined,
      amount_ht_cents: htC,
      vat_rate: vatR,
      notes: fields.notes.trim() || undefined,
    });
  }

  const inputClass =
    'w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-50 disabled:text-slate-400';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Modifier la prestation' : 'Ajouter une prestation'}
      size="md"
    >
      <form onSubmit={handleSubmit} noValidate className="space-y-4">
        {/* Émettrice */}
        <div>
          <label htmlFor="svc-from" className="block text-xs font-medium text-slate-700 mb-1">
            Entité émettrice <span className="text-red-500">*</span>
          </label>
          <select
            id="svc-from"
            className={inputClass}
            value={fields.from_entity_id}
            onChange={(e) => set('from_entity_id', e.target.value)}
          >
            <option value="">— Sélectionner —</option>
            {entities.map((ent) => (
              <option key={ent.id} value={ent.id}>{ent.name}</option>
            ))}
          </select>
          {errors.from_entity_id && (
            <p className="mt-1 text-xs text-red-600">{errors.from_entity_id}</p>
          )}
        </div>

        {/* Débitrice */}
        <div>
          <label htmlFor="svc-to" className="block text-xs font-medium text-slate-700 mb-1">
            Entité débitrice <span className="text-red-500">*</span>
          </label>
          <select
            id="svc-to"
            className={inputClass}
            value={fields.to_entity_id}
            onChange={(e) => set('to_entity_id', e.target.value)}
          >
            <option value="">— Sélectionner —</option>
            {entities.map((ent) => (
              <option key={ent.id} value={ent.id}>{ent.name}</option>
            ))}
          </select>
          {errors.to_entity_id && (
            <p className="mt-1 text-xs text-red-600">{errors.to_entity_id}</p>
          )}
        </div>

        {/* Label */}
        <div>
          <label htmlFor="svc-label" className="block text-xs font-medium text-slate-700 mb-1">
            Label
          </label>
          <input
            id="svc-label"
            type="text"
            className={inputClass}
            value={fields.label}
            onChange={(e) => set('label', e.target.value)}
            placeholder="ex. Mise à disposition commerciale"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          {/* Montant HT */}
          <div>
            <label htmlFor="svc-ht" className="block text-xs font-medium text-slate-700 mb-1">
              Montant HT (€) <span className="text-red-500">*</span>
            </label>
            <input
              id="svc-ht"
              type="text"
              inputMode="decimal"
              className={inputClass}
              value={fields.amount_ht_euros}
              onChange={(e) => set('amount_ht_euros', e.target.value)}
              placeholder="0,00"
            />
            {errors.amount_ht_euros && (
              <p className="mt-1 text-xs text-red-600">{errors.amount_ht_euros}</p>
            )}
          </div>

          {/* TVA % */}
          <div>
            <label htmlFor="svc-vat" className="block text-xs font-medium text-slate-700 mb-1">
              TVA (%)
            </label>
            <input
              id="svc-vat"
              type="number"
              min="0"
              max="100"
              step="0.1"
              className={inputClass}
              value={fields.vat_rate}
              onChange={(e) => set('vat_rate', e.target.value)}
            />
            {errors.vat_rate && (
              <p className="mt-1 text-xs text-red-600">{errors.vat_rate}</p>
            )}
          </div>
        </div>

        {/* Live TTC */}
        {ttcCents != null && (
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-slate-500">TTC calculé</span>
            <span className="text-sm font-semibold text-slate-900 tabular-nums">
              {formatCurrency(ttcCents)}
            </span>
          </div>
        )}

        {/* Notes */}
        <div>
          <label htmlFor="svc-notes" className="block text-xs font-medium text-slate-700 mb-1">
            Notes
          </label>
          <textarea
            id="svc-notes"
            rows={2}
            className={inputClass}
            value={fields.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Informations complémentaires…"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={isPending}
            className="rounded-lg bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50"
          >
            {isPending ? 'Enregistrement...' : isEdit ? 'Sauvegarder' : 'Ajouter'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
